import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, LogOut, Inbox, Truck, CalendarDays, Wallet, Menu, FileText, Users, Settings, Trash2, ClipboardSignature, Globe, RefreshCcw, Clock, Phone, MapPin, Eye, Bell, X, ChevronDown, CheckCircle2, Handshake, Send, Percent, Crown } from "lucide-react";
import { Logo } from "@/components/Logo";
import { AdminLiveMap } from "@/components/AdminLiveMap";
import { PushEnable } from "@/components/mover/PushEnable";
import { TrashBin } from "@/components/TrashBin";
import { ReviewsManager } from "@/components/ReviewsManager";
import { ConvertQuote } from "@/components/ConvertQuote";
import { SendQuoteModal } from "@/components/SendQuoteModal";
import { JobsCalendar, statusLabel } from "@/components/JobsCalendar";
import { JobDetailModal } from "@/components/JobDetailModal";

import { MyEarnings } from "@/components/MyEarnings";
import { Movers } from "@/components/Movers";
import { BookingSettings } from "@/components/BookingSettings";
import { AddJobModal } from "@/components/AddJobModal";
import { MoversBuddy } from "@/components/MoversBuddy";
import { ManualJobs } from "@/components/ManualJobs";
import { DeliveryPlus } from "@/components/DeliveryPlus";
import { MoverRoster } from "@/components/MoverRoster";
import { Customers } from "@/components/Customers";
import { EmailTemplates } from "@/components/EmailTemplates";
import { ChangePasswordModal } from "@/components/ChangePasswordModal";
import { ContractorContract } from "@/components/ContractorContract";
import { ContractorBreakdown } from "@/components/ContractorBreakdown";
import { Hand } from "lucide-react";
import { Receipt, KeyRound } from "lucide-react";
import { Mail } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const STATUS_STYLES = {
  new: "border-blaze/50 bg-blaze/10 text-blaze",
  contacted: "border-yellow-500/50 bg-yellow-500/10 text-yellow-700",
  booked: "border-green-500/50 bg-green-500/10 text-green-700",
  rejected: "border-red-500/50 bg-red-500/10 text-red-600",
};

const TRUCK_LABELS = { small: "Small 3T", medium: "Medium 6T", large: "Large 10T", unsure: "Not sure" };

const fmtTimerClock = (secs) => {
  const s = Math.max(0, Math.floor(secs));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
};

const ActiveTimersBar = ({ onOpen }) => {
  const [timers, setTimers] = useState([]);
  const [open, setOpen] = useState(true);
  const [, setTick] = useState(0);
  useEffect(() => {
    let live = true;
    const load = () =>
      axios.get(`${API}/admin/active-timers`).then(({ data }) => live && setTimers(data)).catch(() => {});
    load();
    const poll = setInterval(load, 15000);
    const tick = setInterval(() => setTick((n) => n + 1), 1000);
    return () => { live = false; clearInterval(poll); clearInterval(tick); };
  }, []);
  if (!timers.length) return null;
  const elapsedOf = (t) => (t.seconds || 0) + (t.started_at ? (Date.now() - new Date(t.started_at).getTime()) / 1000 : 0);
  return (
    <div className="fixed inset-x-0 bottom-[70px] z-40 px-3 pb-1" data-testid="active-timers-bar">
      <div className="mx-auto max-w-2xl overflow-hidden rounded-2xl border border-emerald-300 bg-white/95 shadow-[0_8px_30px_rgba(0,0,0,0.18)] backdrop-blur-xl">
        <button
          onClick={() => setOpen((v) => !v)}
          className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left"
          data-testid="active-timers-header"
        >
          <span className="relative flex h-2.5 w-2.5 shrink-0">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500" />
          </span>
          <span className="text-sm font-bold text-slate-900">
            {timers.length} job{timers.length === 1 ? "" : "s"} on the clock
          </span>
          <span className="ml-auto text-[10px] font-bold uppercase tracking-wider text-slate-400">{open ? "Hide" : "Show"}</span>
          <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
        {open && (
          <div className="max-h-52 overflow-y-auto border-t border-emerald-100">
            {timers.map((t) => (
              <button
                key={t.job_id}
                onClick={() => onOpen(t.job_id)}
                className="flex w-full items-center gap-3 border-b border-slate-100 px-4 py-2.5 text-left last:border-b-0 hover:bg-emerald-50/60"
                data-testid={`active-timer-${t.job_id}`}
              >
                <Clock className="h-4 w-4 shrink-0 text-emerald-600" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-slate-900">
                    {(t.movers || []).join(", ") || "Crew"}
                    <span className="font-normal text-slate-400"> · {t.job_id}</span>
                  </p>
                  <p className="truncate text-[11px] text-slate-500">{t.customer || t.suburb || "On a job"}</p>
                </div>
                <span className="shrink-0 font-mono text-sm font-bold tabular-nums text-emerald-600" data-testid={`active-timer-clock-${t.job_id}`}>
                  {fmtTimerClock(elapsedOf(t))}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const TomorrowPopup = ({ onClose }) => {
  const [jobs, setJobs] = useState(null);
  useEffect(() => {
    const t = new Date(Date.now() + 86400000).toLocaleDateString("en-CA");
    axios.get(`${API}/bookings/by-date`, { params: { date: t } }).then(({ data }) => setJobs(data)).catch(() => setJobs([]));
  }, []);
  if (!jobs || !jobs.length) return null;
  const label = new Date(Date.now() + 86400000).toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" });
  const unassigned = jobs.filter((j) => !(j.assigned_movers || []).length).length;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-5" data-testid="tomorrow-popup">
      <div className="w-full max-w-md border border-blaze/50 bg-coal p-8">
        <p className="text-xs font-bold uppercase tracking-[0.25em] text-blaze">Tomorrow — {label}</p>
        <h3 className="mt-2 font-display text-3xl font-semibold tracking-tight text-slate-900">
          {jobs.length} Job{jobs.length === 1 ? "" : "s"} Booked
        </h3>
        {unassigned > 0 && (
          <p className="mt-2 text-xs font-bold uppercase tracking-widest text-yellow-700" data-testid="tomorrow-unassigned">
            {unassigned} job{unassigned === 1 ? "" : "s"} not assigned yet
          </p>
        )}
        <div className="mt-6 space-y-2.5">
          {jobs.map((j) => (
            <div key={j.job_id} className="flex items-center justify-between border border-line px-4 py-3 text-sm">
              <span className="font-semibold text-slate-900">{j.job_id} — {j.start_time} — {j.movers || 2} mover{(j.movers || 2) === 1 ? "" : "s"}</span>
              {!(j.assigned_movers || []).length && <span className="text-[10px] font-bold uppercase tracking-widest text-yellow-700">Unassigned</span>}
            </div>
          ))}
        </div>
        <button onClick={onClose} className="mt-6 w-full bg-blaze py-3 text-xs font-bold uppercase tracking-widest text-white" data-testid="tomorrow-close-btn">
          Got It
        </button>
      </div>
    </div>
  );
};

const BookingsList = ({ onView, onAddJob, refreshKey, isContractor }) => {
  const [bookings, setBookings] = useState(null);
  const [showCompleted, setShowCompleted] = useState(false);
  const [showCancelled, setShowCancelled] = useState(false);
  useEffect(() => {
    axios.get(`${API}/bookings`).then(({ data }) => setBookings(data)).catch(() => setBookings([]));
  }, [refreshKey]);

  const deleteJob = async (jobId) => {
    if (!window.confirm(`Move job ${jobId} to the Bin? You can restore it anytime from the Bin tab.`)) return;
    try {
      await axios.delete(`${API}/bookings/${jobId}`);
      setBookings((bs) => bs.filter((b) => b.job_id !== jobId));
    } catch {
      alert("Could not delete the job. Please try again.");
    }
  };
  const parseStartMin = (t) => {
    if (!t) return 0;
    const m = String(t).trim().match(/(\d{1,2}):?(\d{2})?\s*(am|pm)?/i);
    if (!m) return 0;
    let h = parseInt(m[1], 10); const mm = m[2] ? parseInt(m[2], 10) : 0;
    const ap = (m[3] || "").toLowerCase();
    if (ap === "pm" && h < 12) h += 12;
    if (ap === "am" && h === 12) h = 0;
    return h * 60 + mm;
  };
  const hoursUntil = (b) => {
    if (!b.moving_date) return null;
    const [y, mo, d] = b.moving_date.split("-").map(Number);
    const mins = parseStartMin(b.start_time);
    const start = new Date(y, mo - 1, d, Math.floor(mins / 60), mins % 60);
    return (start.getTime() - Date.now()) / 3600000;
  };
  const passJob = async (jobId) => {
    if (!window.confirm(`Pass job ${jobId} back to admin? It will be removed from your jobs.`)) return;
    try {
      await axios.post(`${API}/bookings/${jobId}/pass`);
      toast.success("Job passed back to admin.");
      setBookings((bs) => bs.filter((b) => b.job_id !== jobId));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not pass this job.");
    }
  };
  if (bookings === null) {
    return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-blaze" /></div>;
  }
  if (!bookings.length) {
    return <div className="py-20 text-center text-sm text-slate-500" data-testid="bookings-empty">No confirmed bookings yet — convert a quote or add a job manually.</div>;
  }
  const timeMin = (t) => { const d = Date.parse(`1970-01-01 ${t || ""}`); return isNaN(d) ? Infinity : d; };
  const cmpDate = (a, b) => (a.moving_date || "").localeCompare(b.moving_date || "") || (timeMin(a.start_time) - timeMin(b.start_time));
  const isActive = (b) => b.status !== "completed" && b.status !== "cancelled";
  const assignedList = bookings.filter((b) => isActive(b) && (b.assigned_movers || []).length > 0).sort(cmpDate);
  const unassignedList = bookings.filter((b) => isActive(b) && (b.assigned_movers || []).length === 0).sort(cmpDate);
  const completedList = bookings.filter((b) => b.status === "completed").sort((a, b) => cmpDate(b, a));
  const cancelledList = bookings.filter((b) => b.status === "cancelled").sort((a, b) => cmpDate(b, a));

  const renderCard = (b) => {
    const partner = (b.source === "movers_buddy") || (b.job_id || "").startsWith("MB-") || !!b.ccrm_id;
    return (
          <div key={b.id} className={`rounded-2xl border bg-white p-4 shadow-[0_8px_30px_rgb(0,0,0,0.06)] transition-all duration-200 ease-out hover:-translate-y-0.5 hover:shadow-md ${partner ? "border-slate-200/80" : "border-amber-200 ring-1 ring-amber-100"}`} data-testid={`booking-card-${b.job_id}`}>
            <div className="flex items-center justify-between">
              <p className={`font-display text-base font-bold ${partner ? "text-slate-700" : "text-amber-600"}`}>{b.job_id}</p>
              <div className="flex items-center gap-1.5">
                <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[9px] font-bold uppercase tracking-widest ${partner ? "bg-slate-200 text-slate-600" : "bg-gradient-to-r from-amber-500 to-yellow-400 text-white shadow-sm"}`} data-testid={`booking-source-${b.job_id}`}>
                  {partner ? "Movers Buddy" : <><Crown className="h-3 w-3" /> MoversNearby</>}
                </span>
                <span className={`rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider ${statusLabel(b, isContractor).cls}`}>
                  {statusLabel(b, isContractor).label}
                </span>
              </div>
            </div>
            <p className="mt-2 text-sm font-semibold text-slate-900">{b.first_name} {b.last_name}</p>
            {b.partner_name && <p className="mt-0.5 text-[10px] font-bold uppercase tracking-widest text-sky-700">Partner: {b.partner_name}</p>}
            {!isContractor && (
              <p className="mt-1 inline-flex items-center gap-1 rounded-full bg-blue-50 px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-blaze" data-testid={`booking-contractor-${b.job_id}`}>
                <Users className="h-3 w-3" /> {b.contractor_name ? `Contractor: ${b.contractor_name}` : "Unassigned contractor"}
              </p>
            )}
            {!isContractor && !b.contractor_name && (b.passed_history || []).length > 0 && (
              <p className="mt-1 inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-amber-700" data-testid={`booking-passed-${b.job_id}`}>
                <Hand className="h-3 w-3" /> Passed back by {b.passed_history[b.passed_history.length - 1].contractor_name || "contractor"}
              </p>
            )}
            {b.signed_off_by && <p className="mt-0.5 text-[10px] uppercase tracking-widest text-green-700">Signed off by {b.signed_off_by}</p>}
            {b.crew_status && Object.keys(b.crew_status).length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`crew-live-${b.job_id}`}>
                {Object.entries(b.crew_status).map(([n, s]) => (
                  <span key={n} className={`rounded-full px-2.5 py-1 text-[10px] font-bold ${s.status === "arrived" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>
                    {n}: {s.status === "arrived" ? "Arrived" : "On the way"} · {s.at}
                  </span>
                ))}
              </div>
            )}
            {b.crew_location && Object.keys(b.crew_location).length > 0 && (
              <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid={`crew-loc-${b.job_id}`}>
                {Object.entries(b.crew_location).map(([n, l]) => (
                  <a
                    key={n}
                    href={`https://www.google.com/maps?q=${l.lat},${l.lng}`}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-1 rounded-full bg-blue-50 px-2.5 py-1 text-[10px] font-bold text-blaze underline-offset-2 hover:underline"
                    data-testid={`crew-loc-link-${b.job_id}-${n.toLowerCase()}`}
                  >
                    <MapPin className="h-3 w-3" /> {n}'s location · {l.at}
                  </a>
                ))}
              </div>
            )}
            <div className="mt-3 grid grid-cols-2 gap-1.5 text-xs text-slate-600">
              <p className="rounded-lg bg-slate-50 px-2.5 py-2"><CalendarDays className="mr-1.5 inline h-3.5 w-3.5 text-blaze" />{b.moving_date}</p>
              <p className="rounded-lg bg-slate-50 px-2.5 py-2"><Clock className="mr-1.5 inline h-3.5 w-3.5 text-blaze" />{b.start_time || "—"}</p>
              <p className="rounded-lg bg-slate-50 px-2.5 py-2"><Truck className="mr-1.5 inline h-3.5 w-3.5 text-blaze" />{b.truck || "—"}</p>
              <p className="rounded-lg bg-slate-50 px-2.5 py-2"><Users className="mr-1.5 inline h-3.5 w-3.5 text-blaze" />{b.movers || 2} men{!isContractor && b.estimated_price != null ? ` · $${b.estimated_price}` : ""}</p>
              {(b.pickup_suburb || b.drop_suburb) && (
                <p className="col-span-2 rounded-lg bg-slate-50 px-2.5 py-2"><MapPin className="mr-1.5 inline h-3.5 w-3.5 text-blaze" />{b.pickup_suburb || "—"} → {b.drop_suburb || "—"}</p>
              )}
              {(b.cbd_travel_min || 0) > 0 && (
                <p className="col-span-2 rounded-lg bg-amber-50 px-2.5 py-2 font-bold text-amber-700" data-testid={`cbd-badge-${b.job_id}`}>
                  {Math.max(b.cbd_distance?.pickup_km || 0, b.cbd_distance?.drop_km || 0)} km from Melb CBD · +{b.cbd_travel_min === 60 ? "1 hr" : "30 min"} travel
                </p>
              )}
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2">
              <button
                onClick={() => onView(b.job_id)}
                className="flex items-center justify-center gap-1.5 rounded-lg bg-blaze py-2.5 text-[10px] font-bold uppercase tracking-wider text-white active:scale-95"
                data-testid={`mob-view-${b.job_id}`}
              >
                <Eye className="h-3.5 w-3.5" /> Details
              </button>
              <a
                href={`/signoff?job=${b.job_id}`}
                className="flex items-center justify-center gap-1.5 rounded-lg border border-blaze py-2.5 text-[10px] font-bold uppercase tracking-wider text-blaze active:scale-95"
                data-testid={`mob-signoff-${b.job_id}`}
              >
                Sign Off
              </a>
              <button
                onClick={() => deleteJob(b.job_id)}
                className="flex items-center justify-center gap-1.5 rounded-lg border border-red-300 py-2.5 text-[10px] font-bold uppercase tracking-wider text-red-600 active:scale-95"
                data-testid={`mob-delete-${b.job_id}`}
              >
                <Trash2 className="h-3.5 w-3.5" /> Bin
              </button>
            </div>
            {isContractor && isActive(b) && (() => {
              const hrs = hoursUntil(b);
              const locked = hrs !== null && hrs < 24;
              return (
                <button
                  onClick={() => locked ? toast.error("Less than 24 hours to start — please contact admin to drop this job.") : passJob(b.job_id)}
                  className={`mt-2 flex w-full items-center justify-center gap-1.5 rounded-lg py-2.5 text-[10px] font-bold uppercase tracking-wider active:scale-95 ${locked ? "cursor-not-allowed border border-slate-200 bg-slate-50 text-slate-400" : "border border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100"}`}
                  data-testid={`pass-job-${b.job_id}`}
                >
                  <Hand className="h-3.5 w-3.5" /> {locked ? "Can't pass — contact admin" : "Pass job back"}
                </button>
              );
            })()}
          </div>
    );
  };

  return (
    <>
      <div className="mt-5" data-testid="assigned-jobs-section">
        <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-blaze">
          <Users className="h-4 w-4" /> Assigned Jobs ({assignedList.length})
        </p>
        <div className="mt-3 grid items-start gap-3 md:grid-cols-2 xl:grid-cols-3" data-testid="bookings-cards">
          {assignedList.length === 0 ? (
            <p className="col-span-full rounded-2xl border border-dashed border-line bg-white/60 p-5 text-center text-sm text-slate-500">No assigned jobs.</p>
          ) : (
            assignedList.map(renderCard)
          )}
        </div>
      </div>
      <div className="mt-8" data-testid="unassigned-jobs-section">
        <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-amber-600">
          <Clock className="h-4 w-4" /> Booked — Not Assigned ({unassignedList.length})
        </p>
        <div className="mt-3 grid items-start gap-3 md:grid-cols-2 xl:grid-cols-3" data-testid="bookings-unassigned">
          {unassignedList.length === 0 ? (
            <p className="col-span-full rounded-2xl border border-dashed border-line bg-white/60 p-5 text-center text-sm text-slate-500">Every booked job has a crew assigned. 🎉</p>
          ) : (
            unassignedList.map(renderCard)
          )}
        </div>
      </div>
      {completedList.length > 0 && (
        <div className="mt-8" data-testid="completed-jobs-section">
          <button
            onClick={() => setShowCompleted((s) => !s)}
            className="flex w-full items-center justify-between rounded-2xl border border-green-200 bg-green-50 px-4 py-3.5 transition-colors active:bg-green-100"
            data-testid="toggle-completed-jobs"
          >
            <span className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-green-700">
              <CheckCircle2 className="h-4 w-4" /> Completed Jobs ({completedList.length})
            </span>
            <ChevronDown className={`h-5 w-5 text-green-600 transition-transform duration-200 ${showCompleted ? "rotate-180" : ""}`} />
          </button>
          {showCompleted && (
            <div className="mt-3 grid items-start gap-3 md:grid-cols-2 xl:grid-cols-3" data-testid="bookings-completed">
              {completedList.map(renderCard)}
            </div>
          )}
        </div>
      )}
      {cancelledList.length > 0 && (
        <div className="mt-6" data-testid="cancelled-jobs-section">
          <button
            onClick={() => setShowCancelled((s) => !s)}
            className="flex w-full items-center justify-between rounded-2xl border border-red-200 bg-red-50 px-4 py-3.5 transition-colors active:bg-red-100"
            data-testid="toggle-cancelled-jobs"
          >
            <span className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-red-600">
              <X className="h-4 w-4" /> Cancelled Jobs ({cancelledList.length})
            </span>
            <ChevronDown className={`h-5 w-5 text-red-500 transition-transform duration-200 ${showCancelled ? "rotate-180" : ""}`} />
          </button>
          {showCancelled && (
            <div className="mt-3 grid items-start gap-3 md:grid-cols-2 xl:grid-cols-3" data-testid="bookings-cancelled">
              {cancelledList.map(renderCard)}
            </div>
          )}
        </div>
      )}
    </>
  );
};

const Contractors = () => {
  const [list, setList] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", password: "", share_percent: 70 });
  const [creating, setCreating] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", email: "", share_percent: 70 });
  const [feeFor, setFeeFor] = useState(null);
  const [feeForm, setFeeForm] = useState({ job_id: "", amount: 200, note: "" });

  const load = () => {
    axios.get(`${API}/contractors`).then(({ data }) => setList(data)).catch(() => {});
    axios.get(`${API}/bookings`).then(({ data }) => setJobs(data.filter((b) => b.status !== "cancelled"))).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await axios.post(`${API}/contractors`, { ...form, share_percent: Number(form.share_percent) });
      toast.success("Contractor created.");
      setForm({ name: "", email: "", password: "", share_percent: 70 });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not create contractor."); }
    finally { setCreating(false); }
  };
  const startEdit = (c) => { setEditId(c.id); setEditForm({ name: c.name, email: c.email, share_percent: c.share_percent, password: "" }); };
  const saveEdit = async (id) => {
    try {
      const payload = { name: editForm.name, email: editForm.email, share_percent: Number(editForm.share_percent) };
      if (editForm.password) payload.password = editForm.password;
      await axios.put(`${API}/contractors/${id}`, payload);
      setEditId(null); toast.success(editForm.password ? "Contractor updated & password reset." : "Contractor updated."); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not update."); }
  };
  const remove = async (id, name) => {
    if (!window.confirm(`Remove contractor ${name}? Their jobs return to your pool.`)) return;
    try { await axios.delete(`${API}/contractors/${id}`); toast.success("Contractor removed."); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Could not remove."); }
  };
  const grant = async (id, days) => {
    try { await axios.post(`${API}/contractors/${id}/grant`, { days }); toast.success(`Access granted for ${days} days.`); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Could not grant."); }
  };
  const revokeGrant = async (id) => {
    try { await axios.post(`${API}/contractors/${id}/grant`, { revoke: true }); toast.success("Access revoked."); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Could not revoke."); }
  };
  const submitFee = async (id) => {
    try {
      await axios.post(`${API}/contractors/${id}/no-show-fee`, { job_id: feeForm.job_id || null, amount: Number(feeForm.amount), note: feeForm.note });
      toast.success("No-show fee recorded."); setFeeFor(null); setFeeForm({ job_id: "", amount: 200, note: "" }); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not record fee."); }
  };

  return (
    <div className="mt-6 space-y-6" data-testid="admin-contractors-tab">
      <div>
        <h2 className="font-display text-xl font-bold text-slate-900">Contractors</h2>
        <p className="mt-0.5 text-sm text-slate-500">Create contractors, edit their details, and manage their signed contract & access. Assign a contractor from each job's detail screen.</p>
      </div>

      <form onSubmit={create} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="contractor-create-form">
        <p className="mb-3 text-xs font-bold uppercase tracking-widest text-slate-400">Add contractor</p>
        <div className="grid gap-3 sm:grid-cols-2">
          <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name" className="rounded-xl border border-slate-200 px-3 py-2.5 text-sm" data-testid="contractor-name-input" />
          <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Login email" className="rounded-xl border border-slate-200 px-3 py-2.5 text-sm" data-testid="contractor-email-input" />
          <input required type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Password (6+ chars)" className="rounded-xl border border-slate-200 px-3 py-2.5 text-sm" data-testid="contractor-password-input" />
          <div className="flex items-center gap-2">
            <input required type="number" min="0" max="100" value={form.share_percent} onChange={(e) => setForm({ ...form, share_percent: e.target.value })} className="w-24 rounded-xl border border-slate-200 px-3 py-2.5 text-sm" data-testid="contractor-share-input" />
            <span className="text-sm text-slate-500">% contractor share (rest is admin)</span>
          </div>
        </div>
        <button type="submit" disabled={creating} className="mt-4 rounded-xl bg-blaze px-5 py-3 text-xs font-bold uppercase tracking-widest text-white active:scale-95 disabled:opacity-50" data-testid="contractor-create-btn">
          {creating ? "Adding…" : "Add contractor"}
        </button>
      </form>

      <div className="space-y-2" data-testid="contractor-list">
        {list.length === 0 ? <p className="rounded-2xl border border-dashed border-line p-6 text-center text-sm text-slate-500">No contractors yet.</p> : list.map((c) => {
          const statusUi = c.contract_status === "active"
            ? { cls: "bg-green-100 text-green-700", label: "Contract signed" }
            : c.contract_status === "temporary"
            ? { cls: "bg-amber-100 text-amber-700", label: "Temp access" }
            : { cls: "bg-red-100 text-red-600", label: "Not signed" };
          return (
          <div key={c.id} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm" data-testid={`contractor-${c.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                {editId === c.id ? (
                  <div className="grid gap-2 sm:grid-cols-3">
                    <input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} placeholder="Name" className="rounded-lg border border-slate-200 px-2 py-1.5 text-sm" data-testid={`contractor-name-edit-${c.id}`} />
                    <input type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} placeholder="Email" className="rounded-lg border border-slate-200 px-2 py-1.5 text-sm" data-testid={`contractor-email-edit-${c.id}`} />
                    <input type="number" min="0" max="100" value={editForm.share_percent} onChange={(e) => setEditForm({ ...editForm, share_percent: e.target.value })} placeholder="% share" className="rounded-lg border border-slate-200 px-2 py-1.5 text-sm" data-testid={`contractor-share-edit-${c.id}`} />
                    <input type="text" value={editForm.password || ""} onChange={(e) => setEditForm({ ...editForm, password: e.target.value })} placeholder="New password (leave blank to keep)" className="rounded-lg border border-slate-200 px-2 py-1.5 text-sm" data-testid={`contractor-password-edit-${c.id}`} />
                  </div>
                ) : (
                  <>
                    <p className="font-display text-base font-bold text-slate-900">{c.name}</p>
                    <p className="truncate text-xs text-slate-500">{c.email} · {c.movers} movers · {c.jobs} jobs</p>
                    <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                      <span className="rounded-full bg-blue-50 px-2.5 py-1 text-[11px] font-bold text-blaze">{c.share_percent}% share</span>
                      <span className={`rounded-full px-2.5 py-1 text-[11px] font-bold ${statusUi.cls}`} data-testid={`contractor-contract-status-${c.id}`}>{statusUi.label}</span>
                      {c.fees_total > 0 && <span className="rounded-full bg-red-50 px-2.5 py-1 text-[11px] font-bold text-red-600" data-testid={`contractor-fees-${c.id}`}>${c.fees_total} fees · {c.fees_count}</span>}
                    </div>
                  </>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {editId === c.id ? (
                  <>
                    <button onClick={() => saveEdit(c.id)} className="rounded-lg bg-green-600 px-3 py-1.5 text-xs font-bold text-white" data-testid={`contractor-save-${c.id}`}>Save</button>
                    <button onClick={() => setEditId(null)} className="rounded-lg bg-slate-100 px-3 py-1.5 text-xs font-bold text-slate-600">Cancel</button>
                  </>
                ) : (
                  <>
                    <button onClick={() => startEdit(c)} className="rounded-lg bg-slate-100 px-3 py-1.5 text-xs font-bold text-slate-600" data-testid={`contractor-edit-${c.id}`}>Edit</button>
                    <button onClick={() => remove(c.id, c.name)} className="rounded-lg bg-red-50 px-3 py-1.5 text-xs font-bold text-red-500" data-testid={`contractor-delete-${c.id}`}>Remove</button>
                  </>
                )}
              </div>
            </div>
            {c.contract_status !== "active" && editId !== c.id && (
              <div className="mt-3 flex flex-wrap items-center gap-2 rounded-xl bg-slate-50 px-3 py-2" data-testid={`contractor-grant-${c.id}`}>
                <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Grant access:</span>
                {[7, 14, 30].map((d) => (
                  <button key={d} onClick={() => grant(c.id, d)} className="rounded-lg border border-blaze px-2.5 py-1 text-[11px] font-bold text-blaze active:scale-95" data-testid={`grant-${d}-${c.id}`}>{d} days</button>
                ))}
                {c.contract_status === "temporary" && <button onClick={() => revokeGrant(c.id)} className="rounded-lg bg-slate-200 px-2.5 py-1 text-[11px] font-bold text-slate-600">Revoke</button>}
              </div>
            )}
            {feeFor === c.id && false && (
              <div className="mt-3 grid gap-2 rounded-xl border border-amber-200 bg-amber-50/60 p-3 sm:grid-cols-4" data-testid={`fee-form-${c.id}`}>
                <select value={feeForm.job_id} onChange={(e) => setFeeForm({ ...feeForm, job_id: e.target.value })} className="rounded-lg border border-slate-200 px-2 py-1.5 text-sm" data-testid={`fee-job-${c.id}`}>
                  <option value="">Job (optional)</option>
                  {jobs.map((b) => <option key={b.job_id} value={b.job_id}>{b.job_id} · {b.first_name}</option>)}
                </select>
                <input type="number" min="0" value={feeForm.amount} onChange={(e) => setFeeForm({ ...feeForm, amount: e.target.value })} className="rounded-lg border border-slate-200 px-2 py-1.5 text-sm" data-testid={`fee-amount-${c.id}`} />
                <input value={feeForm.note} onChange={(e) => setFeeForm({ ...feeForm, note: e.target.value })} placeholder="Note (e.g. late cancel)" className="rounded-lg border border-slate-200 px-2 py-1.5 text-sm" data-testid={`fee-note-${c.id}`} />
                <button onClick={() => submitFee(c.id)} className="rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-bold text-white" data-testid={`fee-save-${c.id}`}>Record fee</button>
              </div>
            )}
          </div>
          );
        })}
      </div>
    </div>
  );
};


export default function Admin() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [quotes, setQuotes] = useState([]);
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("new");
  const [dateFilter, setDateFilter] = useState("");
  const [tab, setTab] = useState("bookings");
  const isOwner = user?.role === "admin";
  const [convertNum, setConvertNum] = useState("");
  const [detailJob, setDetailJob] = useState(null);
  const [showTomorrow, setShowTomorrow] = useState(() => {
    try {
      return localStorage.getItem("ym-tomorrow-dismissed") !== new Date().toLocaleDateString("en-CA");
    } catch {
      return true;
    }
  });
  const [showAddJob, setShowAddJob] = useState(false);
  const [addJobPrefill, setAddJobPrefill] = useState({});
  const [showChangePw, setShowChangePw] = useState(false);
  const [sendQuote, setSendQuote] = useState(null);
  const [resendingId, setResendingId] = useState(null);
  const [bookingsRefresh, setBookingsRefresh] = useState(0);
  const [moreOpen, setMoreOpen] = useState(false);
  const [notifs, setNotifs] = useState({ unread: 0, items: [] });
  const [showNotifs, setShowNotifs] = useState(false);
  const seenNotifIds = useRef(null);

  const NOTIF_ICON = { new_quote: "📋", on_the_way: "🚚", arrived: "📍", job_completed: "✅", tomorrow_jobs: "📅", agreement_submitted: "✍️", contractor_contract_signed: "✍️", job_passed: "↩️" };

  const loadNotifs = () =>
    axios.get(`${API}/notifications`).then(({ data }) => {
      const ids = new Set((data.items || []).map((i) => i.id));
      if (seenNotifIds.current === null) {
        seenNotifIds.current = ids; // first load — don't toast existing
      } else {
        (data.items || [])
          .filter((i) => !i.read && !seenNotifIds.current.has(i.id))
          .slice(0, 5)
          .forEach((i) => toast(`${NOTIF_ICON[i.type] || "🔔"}  ${i.message}`, { duration: 7000 }));
        seenNotifIds.current = ids;
      }
      setNotifs(data);
    }).catch(() => {});

  const openNotifs = async () => {
    setShowNotifs(true);
    if (notifs.unread > 0) {
      try {
        await axios.post(`${API}/notifications/read`);
        setNotifs((n) => ({ ...n, unread: 0, items: n.items.map((i) => ({ ...i, read: true })) }));
      } catch {}
    }
  };

  useEffect(() => {
    if (!showNotifs) return;
    const onKey = (e) => e.key === "Escape" && setShowNotifs(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showNotifs]);

  const loadQuotes = async () => {
    try {
      const { data } = await axios.get(`${API}/quotes`);
      setQuotes(Array.isArray(data) ? data : []);
    } catch {
      toast.error("Could not load quotes. Please refresh.");
    }
  };

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/quotes/${id}/status`, { status });
      setQuotes((qs) => qs.map((q) => (q.id === id ? { ...q, status } : q)));
    } catch {
      alert("Could not update status. Please try again.");
    }
  };

  const deleteQuote = async (q) => {
    if (!window.confirm(`Move quote ${q.quote_number || q.id} to the Bin? You can restore it anytime from the Bin tab.`)) return;
    try {
      await axios.delete(`${API}/quotes/${q.id}`);
      setQuotes((qs) => qs.filter((x) => x.id !== q.id));
    } catch {
      alert("Could not delete the quote. Please try again.");
    }
  };

  const resendQuote = async (q) => {
    setResendingId(q.id);
    try {
      const { data } = await axios.post(`${API}/quotes/send`, { quote_id: q.id, email: q.email });
      toast.success(`Quote ${data.quote_number} re-sent to ${data.sent_to} ✉️`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not resend the quote.");
    } finally {
      setResendingId(null);
    }
  };

  useEffect(() => {
    axios.get(`${API}/auth/me`)
      .then(({ data }) => {
        if (!["admin", "contractor"].includes(data.role)) {
          window.location.href = "/mover";
          return;
        }
        setUser(data);
        loadNotifs();
        return loadQuotes();
      })
      .catch(() => { window.location.href = "/login"; })
      .finally(() => setChecking(false));
  }, []);

  useEffect(() => {
    if (!user) return;
    const t = setInterval(loadNotifs, 30000);
    return () => clearInterval(t);
  }, [user]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, form);
      if (!["admin", "contractor"].includes(data.role)) {
        setError("This is the admin login. Movers: please use the Mover Login page to see your jobs and payments.");
        return;
      }
      setUser(data);
      loadNotifs();
      await loadQuotes();
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Login failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try { await axios.post(`${API}/auth/logout`); } catch { /* ignore */ }
    window.location.href = "/login";
  };

  const visible = quotes.filter(
    (q) =>
      (statusFilter === "all" || q.status === statusFilter) &&
      (!dateFilter || q.moving_date === dateFilter)
  );

  if (checking) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink">
        <Loader2 className="h-8 w-8 animate-spin text-blaze" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink" data-testid="admin-login-page">
        <Loader2 className="h-8 w-8 animate-spin text-blaze" />
      </div>
    );
  }

  if (user.role === "contractor" && user.contract_status === "locked") {
    return (
      <ContractorContract
        onSigned={() => axios.get(`${API}/auth/me`).then(({ data }) => setUser(data)).catch(() => {})}
        onLogout={logout}
      />
    );
  }

  return (
    <div className="min-h-screen bg-ink px-4 pb-28 pt-6 md:px-5 md:pt-10 lg:px-10" data-testid="admin-dashboard">
      <div className="mx-auto max-w-6xl">
        <div className="sticky top-3 z-40 flex items-center justify-between gap-4 rounded-2xl border border-slate-200/80 bg-white/85 px-4 py-3.5 shadow-[0_8px_30px_rgb(0,0,0,0.06)] backdrop-blur-xl md:px-5">
          <div className="min-w-0">
            <h1 className="truncate font-display text-xl font-semibold uppercase tracking-tight text-slate-900 md:text-3xl">
              Admin <span className="text-blaze">Dashboard</span>
            </h1>
            <p className="mt-0.5 truncate text-xs text-slate-500">{quotes.length} quotes — {user.email}</p>
          </div>
          <button onClick={openNotifs} className="relative rounded-full border border-slate-200 bg-white p-3 text-slate-600 shadow-sm transition-transform active:scale-95" data-testid="admin-notif-bell">
            <Bell className="h-5 w-5" />
            {notifs.unread > 0 && (
              <span className="absolute -right-1 -top-1 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-bold text-white" data-testid="admin-notif-badge">
                {notifs.unread}
              </span>
            )}
          </button>
        </div>

        <PushEnable
          subscribePath="/admin/push/subscribe"
          unsubscribePath="/admin/push/unsubscribe"
          label="Get a pop-up on your phone for new quotes, movers on the way, sign-offs & tomorrow's jobs."
        />

        {tab === "quotes" && (
        <>
        <div className="mt-6 flex flex-wrap items-center gap-2.5" data-testid="admin-filters">
          {["new", "contacted", "booked", "all"].map((s) => {
            const count = s === "all" ? quotes.length : quotes.filter((q) => q.status === s).length;
            const active = statusFilter === s;
            return (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                data-testid={`filter-status-${s}`}
                className={`flex items-center gap-2 rounded-full px-4 py-2.5 font-display text-xs font-bold uppercase tracking-widest transition-all ${
                  active
                    ? "bg-blaze text-white shadow-md shadow-blue-500/25"
                    : "border border-slate-300 bg-white text-slate-600 hover:border-blaze hover:text-blaze"
                }`}
              >
                {s}
                <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold ${active ? "bg-white/25 text-white" : "bg-slate-100 text-slate-500"}`}>
                  {count}
                </span>
              </button>
            );
          })}
          <input
            type="date"
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            className="rounded-full border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 outline-none focus:border-blaze"
            data-testid="filter-date-input"
            title="Filter by move date"
          />
          {(statusFilter !== "new" || dateFilter) && (
            <button
              onClick={() => { setStatusFilter("new"); setDateFilter(""); }}
              className="text-xs font-bold uppercase tracking-widest text-slate-500 transition-colors hover:text-blaze"
              data-testid="filter-clear-btn"
            >
              Clear
            </button>
          )}
          <span className="ml-auto text-xs text-slate-500" data-testid="filter-count">
            Showing {visible.length} of {quotes.length}
          </span>
          <button
            onClick={() => setSendQuote({})}
            className="flex items-center gap-1.5 rounded-full bg-blaze px-4 py-2.5 text-xs font-bold uppercase tracking-widest text-white transition-all hover:bg-blue-700 active:scale-95"
            data-testid="send-quote-btn"
          >
            <Send className="h-3.5 w-3.5" /> Send Quote
          </button>
        </div>

        {visible.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-32 text-center" data-testid="admin-empty">
            <Inbox className="mb-4 h-12 w-12 text-slate-700" strokeWidth={1.4} />
            <p className="text-sm text-slate-500">No quote requests yet. They'll appear here as they come in.</p>
          </div>
        ) : (
          <>
          <div className="mt-5 grid items-start gap-3 md:grid-cols-2 xl:grid-cols-3" data-testid="admin-quotes-cards">
            {visible.map((q) => (
              <div key={q.id} className="rounded-2xl border border-slate-200/80 bg-white p-4 shadow-[0_8px_30px_rgb(0,0,0,0.06)] transition-all duration-200 ease-out hover:-translate-y-0.5 hover:shadow-md" data-testid={`quote-card-${q.id}`}>
                <div className="flex items-center justify-between">
                  <p className="font-display text-base font-bold text-blaze">{q.quote_number || "—"}</p>
                  <select
                    value={q.status}
                    onChange={(e) => setStatus(q.id, e.target.value)}
                    className={`cursor-pointer rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider outline-none ${STATUS_STYLES[q.status] || STATUS_STYLES.new}`}
                    data-testid={`mob-status-${q.id}`}
                  >
                    <option value="new">New</option>
                    <option value="contacted">Contacted</option>
                    <option value="booked">Booked</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>
                <p className="mt-2 text-sm font-semibold text-slate-900">{q.pickup_suburb} → {q.drop_suburb}</p>
                <div className="mt-3 grid grid-cols-2 gap-1.5 text-xs text-slate-600">
                  <p className="rounded-lg bg-slate-50 px-2.5 py-2"><CalendarDays className="mr-1.5 inline h-3.5 w-3.5 text-blaze" />{q.moving_date}{q.time_window ? ` · ${q.time_window}` : ""}</p>
                  <p className="rounded-lg bg-slate-50 px-2.5 py-2"><FileText className="mr-1.5 inline h-3.5 w-3.5 text-blaze" />{q.property_size}</p>
                  <p className="rounded-lg bg-slate-50 px-2.5 py-2"><Truck className="mr-1.5 inline h-3.5 w-3.5 text-blaze" />{TRUCK_LABELS[q.truck] || "—"}</p>
                  <p className="rounded-lg bg-slate-50 px-2.5 py-2 text-slate-400">{new Date(q.created_at).toLocaleDateString("en-AU")}</p>
                </div>
                <div className="mt-3 space-y-2">
                  <div className="flex gap-2">
                    <a href={`tel:${q.phone}`} className="flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-green-600 py-2.5 text-[10px] font-bold uppercase tracking-wider text-white active:scale-95" data-testid={`mob-call-${q.id}`}>
                      <Phone className="h-3.5 w-3.5" /> Call
                    </a>
                    <button
                      onClick={() => resendQuote(q)}
                      disabled={resendingId === q.id}
                      className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-blaze py-2.5 text-[10px] font-bold uppercase tracking-wider text-blaze active:scale-95 disabled:opacity-50"
                      data-testid={`mob-resend-quote-${q.id}`}
                    >
                      {resendingId === q.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Send className="h-3.5 w-3.5" /> Resend</>}
                    </button>
                    <button
                      onClick={() => setSendQuote(q)}
                      className="flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-amber-500 py-2.5 text-[10px] font-bold uppercase tracking-wider text-white active:scale-95"
                      data-testid={`mob-discount-quote-${q.id}`}
                    >
                      <Percent className="h-3.5 w-3.5" /> Discount
                    </button>
                  </div>
                  <div className="flex gap-2">
                    {q.status !== "booked" && q.status !== "rejected" && (
                      <button
                        onClick={() => { setConvertNum(q.quote_number); setTab("convert"); }}
                        className="flex flex-1 items-center justify-center rounded-lg bg-blaze py-2.5 text-[10px] font-bold uppercase tracking-wider text-white active:scale-95"
                        data-testid={`mob-book-${q.id}`}
                      >
                        Book
                      </button>
                    )}
                    <button
                      onClick={() => deleteQuote(q)}
                      className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-red-300 py-2.5 text-[10px] font-bold uppercase tracking-wider text-red-600 active:scale-95"
                      data-testid={`mob-delete-quote-${q.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" /> Bin
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
          </>
        )}
        </>
        )}
        {tab === "convert" && <ConvertQuote initialNumber={convertNum} />}
        {tab === "bookings" && (
          <>
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowAddJob(true)}
                className="bg-blaze px-6 py-3 text-xs font-bold uppercase tracking-widest text-white transition-all hover:scale-[1.02]"
                data-testid="add-job-btn"
              >
                + Add Job Manually
              </button>
            </div>
            <BookingsList onView={setDetailJob} refreshKey={bookingsRefresh} isContractor={user?.role === "contractor"} />
          </>
        )}
        {tab === "map" && (
          <div className="mt-6" data-testid="admin-map-tab">
            <AdminLiveMap />
          </div>
        )}
        {tab === "calendar" && <JobsCalendar onView={setDetailJob} isContractor={!isOwner} />}
        {tab === "payments" && !isOwner && <MyEarnings onView={setDetailJob} />}
        {tab === "movers" && <Movers />}
        {tab === "contractors" && <Contractors />}
        {tab === "contractorshare" && isOwner && (
          <div className="mt-6 space-y-4" data-testid="admin-contractor-share-tab">
            <div>
              <h2 className="font-display text-xl font-bold text-slate-900">Contractor Share</h2>
              <p className="mt-0.5 text-sm text-slate-500">Each contractor's share from MoversNearby jobs, per week or day. Tap a period to see jobs & mark paid.</p>
            </div>
            <ContractorBreakdown />
          </div>
        )}
        {tab === "buddy" && user?.partner_access && <MoversBuddy onImported={() => setBookingsRefresh((k) => k + 1)} />}
        {tab === "delivery" && user?.partner_access && <DeliveryPlus />}
        {tab === "oyo" && user?.partner_access && <ManualJobs source="oyo_movers" title="Oyo Movers" subtitle="Manually logged Oyo Movers jobs & payments" />}
        {tab === "roster" && <MoverRoster />}
        {tab === "customers" && <Customers onAddJob={(prefill) => { setAddJobPrefill(prefill); setTab("bookings"); setShowAddJob(true); }} />}
        {tab === "settings" && <><BookingSettings /><ReviewsManager /></>}
        {tab === "emails" && <EmailTemplates />}
        {tab === "bin" && <TrashBin onRestored={() => { loadQuotes(); setBookingsRefresh((k) => k + 1); }} />}
        {detailJob && <JobDetailModal jobId={detailJob} isOwner={isOwner} onClose={() => setDetailJob(null)} />}
        {showAddJob && <AddJobModal initial={addJobPrefill} isOwner={isOwner} onClose={() => { setShowAddJob(false); setAddJobPrefill({}); }} onAdded={() => setBookingsRefresh((k) => k + 1)} />}
        {showChangePw && <ChangePasswordModal onClose={() => setShowChangePw(false)} />}
        {showTomorrow && <TomorrowPopup onClose={() => {
          try { localStorage.setItem("ym-tomorrow-dismissed", new Date().toLocaleDateString("en-CA")); } catch {}
          setShowTomorrow(false);
        }} />}
      </div>

      <ActiveTimersBar onOpen={(jid) => setDetailJob(jid)} />

      {sendQuote !== null && (
        <SendQuoteModal
          initial={sendQuote}
          onClose={(sent) => { setSendQuote(null); if (sent) loadQuotes(); }}
        />
      )}

      <nav className="fixed inset-x-0 bottom-0 z-40 border-t border-slate-200 bg-white/90 pb-[env(safe-area-inset-bottom)] shadow-[0_-8px_30px_rgba(0,0,0,0.08)] backdrop-blur-xl" data-testid="admin-bottom-nav">
        <div className="mx-auto grid max-w-2xl grid-cols-6">
          {[["bookings", "Jobs", Truck], ["quotes", "Quotes", Inbox], ["map", "Map", MapPin], ["calendar", "Calendar", CalendarDays], [isOwner ? "contractorshare" : "payments", isOwner ? "Payouts" : "Earnings", Wallet]].map(([k, l, Icon]) => (
            <button
              key={k}
              onClick={() => { setTab(k); setMoreOpen(false); }}
              className={`flex flex-col items-center gap-1 py-2.5 transition-colors ${tab === k ? "text-blaze" : "text-slate-400"}`}
              data-testid={`mnav-${k}`}
            >
              <span className={`flex h-8 w-12 items-center justify-center rounded-full ${tab === k ? "bg-blue-50" : ""}`}>
                <Icon className="h-5 w-5" strokeWidth={tab === k ? 2.4 : 2} />
              </span>
              <span className="text-[9px] font-bold uppercase tracking-wider">{l}</span>
            </button>
          ))}
          <button
            onClick={() => setMoreOpen(true)}
            className={`flex flex-col items-center gap-1 py-2.5 transition-colors ${["convert", "movers", "settings", "bin", "buddy", "roster", "delivery", "oyo", "contractors", "customers", "emails"].includes(tab) ? "text-blaze" : "text-slate-400"}`}
            data-testid="mnav-more"
          >
            <span className={`flex h-8 w-12 items-center justify-center rounded-full ${["convert", "movers", "settings", "bin", "buddy", "roster", "delivery", "oyo", "contractors", "customers", "emails"].includes(tab) ? "bg-blue-50" : ""}`}>
              <Menu className="h-5 w-5" />
            </span>
            <span className="text-[9px] font-bold uppercase tracking-wider">More</span>
          </button>
        </div>
      </nav>

      {showNotifs && (
        <div className="fixed inset-0 z-50" data-testid="admin-notif-panel">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowNotifs(false)} />
          <div className="absolute inset-x-0 bottom-0 mx-auto max-h-[70vh] max-w-lg overflow-y-auto rounded-t-3xl bg-white p-5 pb-10 sm:inset-x-auto sm:right-6 sm:top-20 sm:bottom-auto sm:w-96 sm:rounded-2xl">
            <div className="flex items-center justify-between">
              <p className="font-display text-lg font-bold text-slate-900">Notifications</p>
              <button onClick={() => setShowNotifs(false)} className="rounded-full bg-slate-100 p-2" data-testid="notif-close"><X className="h-4 w-4 text-slate-600" /></button>
            </div>
            <div className="mt-3 space-y-2">
              {notifs.items.length === 0 ? (
                <p className="py-8 text-center text-sm text-slate-500" data-testid="notif-empty">No notifications yet.</p>
              ) : (
                notifs.items.map((n) => (
                  <div key={n.id} className={`rounded-xl border p-3 text-sm ${n.read ? "border-line text-slate-600" : "border-blaze bg-blue-50 font-semibold text-slate-900"}`} data-testid={`notif-item-${n.id}`}>
                    {n.message}
                    <p className="mt-1 text-[10px] font-normal uppercase tracking-wider text-slate-400">
                      {new Date(n.created_at).toLocaleString("en-AU", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })}
                    </p>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {moreOpen && (
        <div className="fixed inset-0 z-50" data-testid="admin-more-sheet">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMoreOpen(false)} />
          <div className="absolute inset-x-0 bottom-0 mx-auto max-h-[75vh] max-w-lg overflow-y-auto rounded-t-3xl bg-white p-5 pb-10">
            <div className="mx-auto mb-4 h-1.5 w-12 rounded-full bg-slate-200" />
            <div className="space-y-2">
              {[
                ["convert", "Convert Quote", RefreshCcw],
                ["customers", "Customers & Invoices", Receipt],
                ...(isOwner
                  ? [["contractors", "Contractors", Users]]
                  : []),
                ...(user?.partner_access
                  ? [["buddy", "Movers Buddy Leads", Handshake], ["delivery", "Delivery Plus", Truck], ["oyo", "Oyo Movers", Truck]]
                  : []),
                ["roster", "Jobs by Mover", Users],
                ["movers", "Movers & Pay Rates", Users],
                ["settings", "Booking Settings", Settings],
                ["emails", "Email Templates", Mail],
                ["bin", "Bin / Trash", Trash2],
              ].map(([k, l, Icon]) => (
                <button
                  key={k}
                  onClick={() => { setTab(k); setMoreOpen(false); }}
                  className={`flex w-full items-center gap-3 rounded-xl border p-4 text-sm font-semibold active:scale-[0.98] ${tab === k ? "border-blaze bg-blue-50 text-blaze" : "border-line text-slate-800"}`}
                  data-testid={`more-tab-${k}`}
                >
                  <Icon className="h-5 w-5 text-blaze" /> {l}
                </button>
              ))}
              {!isOwner && (
                <button onClick={() => { setShowChangePw(true); setMoreOpen(false); }} className="flex w-full items-center gap-3 rounded-xl border border-line p-4 text-sm font-semibold text-slate-800 active:scale-[0.98]" data-testid="more-change-password">
                  <KeyRound className="h-5 w-5 text-blaze" /> Change My Password
                </button>
              )}
              <a href="/signoff" className="flex w-full items-center gap-3 rounded-xl border border-line p-4 text-sm font-semibold text-slate-800 active:scale-[0.98]" data-testid="more-signoff">
                <ClipboardSignature className="h-5 w-5 text-blaze" /> Customer Sign-Off Page
              </a>
              <a href="/" className="flex w-full items-center gap-3 rounded-xl border border-line p-4 text-sm font-semibold text-slate-800 active:scale-[0.98]" data-testid="more-view-site">
                <Globe className="h-5 w-5 text-blaze" /> View Site
              </a>
              <button onClick={logout} className="flex w-full items-center justify-center gap-2 rounded-xl bg-red-600 p-4 text-sm font-bold uppercase tracking-widest text-white active:scale-[0.98]" data-testid="more-logout">
                <LogOut className="h-4 w-4" /> Logout
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
